import { BAOCustomElement } from '@bao/custom-element'
import { eventBus, EVENTS } from '@bao/events'

const MESSAGE_TYPES = {
  SUCCESS: 'success',
  DUPLICATE: 'duplicate',
  ERROR: 'error',
}

class StoqBis extends BAOCustomElement() {
  static get requiredElements () {
    return ['form', 'email', 'newsletter', 'submit', 'message', 'messageText']
  }

  setupListeners () {
    super.setupListeners()

    this.listeners.add(
      this.els.form.element,
      'submit',
      this.handleSubmit.bind(this),
    )

    this.listeners.add(
      this.els.form.element,
      'input',
      this.handleInput.bind(this),
    )

    eventBus.addEventListener(
      EVENTS.PRODUCT.OPTION_CHANGED,
      this.reset.bind(this),
    )
  }

  handleInput () {
    const message = this.els.message.element

    if (message.dataset.status === MESSAGE_TYPES.ERROR) {
      this.clearMessage()
    }
  }

  async handleSubmit (event) {
    event.preventDefault()

    const form = this.els.form.element
    const emailInput = this.els.email.element
    const consentInput = this.els.newsletter.element
    const footer = this.els.footer.element

    const endpoint = this.dataset.endpoint?.trim()
    const shopDomain = form.dataset.shopDomain?.trim()

    if (form.dataset.available === 'true') {
      this.showMessage(MESSAGE_TYPES.ERROR)

      return
    }

    const productId = Number(form.dataset.productId)
    const variantId = Number(form.dataset.variantId)

    if (!endpoint || !shopDomain || !productId || !variantId) {
      console.error('STOQ BIS: Required configuration is missing.', {
        endpoint,
        shopDomain,
        productId: form.dataset.productId,
        variantId: form.dataset.variantId,
      })

      this.showMessage(MESSAGE_TYPES.ERROR)

      return
    }

    const payload = this.buildPayload(form, {
      productId,
      variantId,
      email: emailInput.value.trim(),
    })

    this.clearMessage()
    this.setLoading(true)

    try {
      const response = await fetch(endpoint, {
        method: 'POST',
        headers: {
          'X-Shopify-Shop-Domain': shopDomain,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      })

      const data = await this.getResponseData(response)
      const errors = this.getResponseErrors(data)

      if (errors.length > 0) {
        if (this.isDuplicateRegistration(errors)) {
          this.showMessage(MESSAGE_TYPES.DUPLICATE)
          return
        }

        throw new Error(errors.join(', '))
      }

      if (!response.ok) {
        throw new Error(`STOQ request failed with status ${response.status}.`)
      }

      form.disabled = true
      footer.hidden = true
      emailInput.setAttribute('readonly', '')
      consentInput.disabled = true

      this.showMessage(MESSAGE_TYPES.SUCCESS)
    } catch (error) {
      console.error('STOQ BIS: Signup failed.', error)

      this.showMessage(MESSAGE_TYPES.ERROR)
    } finally {
      this.setLoading(false)
    }
  }

  buildPayload (form, props) {
    const marketId = Number(form.dataset.marketId)
    const variantCount = Number(form.dataset.variantCount)

    const intent = {
      shopify_variant_id: props.variantId,
      shopify_product_id: props.productId,
      channel: 'email',
      quantity: 1,
      source: 'api',
    }

    if (marketId) {
      intent.shopify_market_id = marketId
    }

    const customer = {
      email: props.email,
      accepts_marketing: this.els.newsletter.element.checked,
      name: form.dataset.customerName || undefined,
      country: form.dataset.country || undefined,
      locale: form.dataset.locale || undefined,
      shopify_customer_id: form.dataset.customerId || undefined,
    }

    const product = {
      title: form.dataset.productTitle || undefined,
      variant_title: form.dataset.variantTitle || undefined,
      vendor: form.dataset.productVendor || undefined,
      sku: form.dataset.variantSku || undefined,
      variant_count: variantCount || undefined,
    }

    return {
      intent,
      customer,
      product,
    }
  }

  async getResponseData (response) {
    try {
      return await response.json()
    } catch {
      return {}
    }
  }

  getResponseErrors (data) {
    return [data?.errors, data?.error].flatMap((errors) => {
      if (!errors) return []

      if (Array.isArray(errors)) {
        return errors.map(String)
      }

      if (typeof errors === 'string') {
        return [errors]
      }

      return Object.entries(errors)
        .filter(([, value]) => Boolean(value))
        .map(([key, value]) => {
          if (value === true) {
            return key
          }

          return `${key}: ${String(value)}`
        })
    })
  }

  isDuplicateRegistration (errors) {
    return errors.some((error) => {
      return (
        /duplicate/i.test(error) || /already[_\s-]*registered/i.test(error)
      )
    })
  }

  setLoading (isLoading) {
    const form = this.els.form.element
    const submitButton = this.els.submit.element

    form.toggleAttribute('aria-busy', isLoading)

    submitButton.disabled = isLoading
  }

  showMessage (type) {
    const messageEl = this.els.message.element
    const messageTextEl = this.els.messageText.element

    const isError = type === MESSAGE_TYPES.ERROR

    messageEl.setAttribute('role', isError ? 'alert' : 'status')
    messageEl.setAttribute('aria-live', isError ? 'assertive' : 'polite')

    let message = this.dataset[`${type}Message`] ?? ''
    if (message) {
      message += ` <span class="lnk-Underline">${this.dataset.close}</a>`
    }

    messageEl.dataset.status = type
    messageTextEl.innerHTML = message
    messageEl.hidden = false
  }

  clearMessage () {
    const message = this.els.message.element
    const messageText = this.els.messageText.element

    messageText.textContent = ''
    message.hidden = true

    delete message.dataset.status
  }

  reset () {
    const form = this.els.form.element
    const emailInput = this.els.email.element
    const consentInput = this.els.newsletter.element
    const footer = this.els.footer.element

    form.hidden = false
    form.disabled = false
    footer.hidden = false
    emailInput.removeAttribute('readonly')
    consentInput.disabled = false

    this.setLoading(false)
    this.clearMessage()
  }
}

if (!customElements.get('stoq-bis')) {
  customElements.define('stoq-bis', StoqBis)
}
